import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { Download, FileText, RefreshCw, Calendar, ChevronDown, BarChart3, Upload, Calculator, Sparkles, Building2, Check, Activity, TrendingUp, Layers } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import TrustScoreGauge from '@/components/dashboard/TrustScoreGauge';
import PillarCard from '@/components/dashboard/PillarCard';
import PillarDrillDown from '@/components/dashboard/PillarDrillDown';
import ComparePeriods from '@/components/dashboard/ComparePeriods';
import WhatIfPlanner from '@/components/dashboard/WhatIfPlanner';
import AnomalyPanel, { AnomalyBadge } from '@/components/dashboard/AnomalyPanel';

function DashboardV2() {
  const navigate = useNavigate();
  const dashboardRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30d');
  const [exporting, setExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [drillDownPillar, setDrillDownPillar] = useState(null);
  const [pillars, setPillars] = useState({});
  const [showCompare, setShowCompare] = useState(false);
  const [showWhatIf, setShowWhatIf] = useState(false);
  const [showAnomalies, setShowAnomalies] = useState(false);
  const [anomalyRefresh, setAnomalyRefresh] = useState(0);
  const [calculating, setCalculating] = useState(false);
  const [calculationMessage, setCalculationMessage] = useState('');
  
  // Organization/Client switcher state
  const [organizations, setOrganizations] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const [switchingOrg, setSwitchingOrg] = useState(false);
  const orgDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target)) {
        setShowOrgDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (currentOrg) {
      loadDashboardData();
    }
  }, [dateRange, currentOrg]);

  const loadOrganizations = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      const response = await axios.get('/organizations/');
      setOrganizations(response.data);
      
      // Get current user's organization from token or API
      const userOrgId = localStorage.getItem('organization_id');
      const currentOrganization = response.data.find(org => org.id === userOrgId) || response.data[0];
      setCurrentOrg(currentOrganization);
    } catch (err) {
      console.error('Failed to load organizations:', err);
    }
  };

  const switchOrganization = async (org) => {
    try {
      setSwitchingOrg(true);
      const response = await axios.post(`/organizations/switch/${org.id}`);
      
      // Update tokens with new organization context
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      // Update local storage and state
      localStorage.setItem('organization_id', org.id);
      setCurrentOrg(org);
      setShowOrgDropdown(false);
      
      // Reload dashboard data for new organization
      await loadDashboardData();
    } catch (err) {
      console.error('Failed to switch organization:', err);
      setError('Failed to switch client');
    } finally {
      setSwitchingOrg(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Load summary
      const summaryResponse = await axios.get('/dashboard/summary');
      setSummary(summaryResponse.data);

      // Load trend data
      const endDate = new Date();
      const startDate = new Date();
      
      switch(dateRange) {
        case '7d':
          startDate.setDate(endDate.getDate() - 7);
          break;
        case '30d':
          startDate.setDate(endDate.getDate() - 30);
          break;
        case '90d':
          startDate.setDate(endDate.getDate() - 90);
          break;
        case '1y':
          startDate.setFullYear(endDate.getFullYear() - 1);
          break;
        default:
          startDate.setDate(endDate.getDate() - 30);
      }

      const trendResponse = await axios.get('/dashboard/trend', {
        params: {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          interval: 'daily'
        }
      });
      
      setTrendData(trendResponse.data.trend || []);

      // Load pillars for drill-down
      try {
        const pillarsResponse = await axios.get('/admin/categories');
        const pillarMap = {};
        pillarsResponse.data.forEach(p => {
          if (!p.parent_pillar_id) {
            pillarMap[p.name.toLowerCase()] = p.id;
          }
        });
        setPillars(pillarMap);
      } catch (pillarErr) {
        console.warn('Could not load pillars:', pillarErr);
      }

      setError('');
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post('/auth/logout');
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      navigate('/login');
    }
  };

  const recalculateScore = async () => {
    try {
      setCalculating(true);
      setCalculationMessage('');
      setError('');

      // Get current month date range
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const response = await axios.post('/calculations/trigger', {
        period_start: periodStart.toISOString(),
        period_end: periodEnd.toISOString(),
        dry_run: false
      });

      if (response.data.status === 'completed') {
        setCalculationMessage(`Calculation complete! New Trust Score: ${response.data.result?.score?.toFixed(1) || 'N/A'}`);
        // Reload dashboard data to show new score
        await loadDashboardData();
      } else if (response.data.status === 'failed') {
        setError(response.data.error_message || 'Calculation failed');
      } else {
        setCalculationMessage('Calculation triggered. Refreshing data...');
        // Wait a moment then reload
        setTimeout(async () => {
          await loadDashboardData();
          setCalculationMessage('');
        }, 2000);
      }
    } catch (err) {
      console.error('Calculation error:', err);
      setError(err.response?.data?.detail || 'Failed to trigger calculation');
    } finally {
      setCalculating(false);
    }
  };

  const exportCSV = async () => {
    try {
      setExporting(true);
      setExportType('csv');
      
      const response = await axios.post('/exports/csv', {}, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `trust_index_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export CSV');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };

  const exportPDF = async () => {
    if (!dashboardRef.current) return;
    
    try {
      setExporting(true);
      setExportType('pdf');
      
      // Capture the dashboard as canvas
      const canvas = await html2canvas(dashboardRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#f9fafb'
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Add title
      pdf.setFontSize(20);
      pdf.setTextColor(88, 28, 135); // Purple
      pdf.text('Trust Index Report', 14, 15);
      
      // Add date
      pdf.setFontSize(10);
      pdf.setTextColor(107, 114, 128);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, 14, 22);
      
      // Add summary stats
      pdf.setFontSize(12);
      pdf.setTextColor(31, 41, 55);
      pdf.text(`Overall Trust Score: ${summary?.trust_score?.toFixed(1) || '—'}`, 14, 32);
      pdf.text(`Data Completeness: ${summary?.data_completeness?.toFixed(1) || '0'}%`, 14, 38);
      
      // Add image
      const imgWidth = pageWidth - 28;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Check if image fits, otherwise scale
      const maxImgHeight = pageHeight - 50;
      const finalImgHeight = Math.min(imgHeight, maxImgHeight);
      const finalImgWidth = (finalImgHeight === maxImgHeight) 
        ? (canvas.width * maxImgHeight) / canvas.height 
        : imgWidth;
      
      pdf.addImage(imgData, 'PNG', 14, 45, finalImgWidth, finalImgHeight);
      
      // Save PDF
      pdf.save(`trust_index_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF');
    } finally {
      setExporting(false);
      setExportType(null);
    }
  };

  const handlePillarDrillDown = (pillarName) => {
    const pillarId = pillars[pillarName.toLowerCase()];
    if (pillarId) {
      setDrillDownPillar({ id: pillarId, name: pillarName });
    } else {
      console.error('Pillar ID not found for:', pillarName, 'Available pillars:', pillars);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" data-testid="loading-spinner"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!summary || !summary.has_data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-white shadow-sm sticky top-0 z-40" data-testid="dashboard-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Trust Index Dashboard</h1>
                  <p className="text-sm text-gray-500">Real-time brand trust measurement</p>
                </div>
                
                {/* Organization/Client Switcher */}
                <div className="relative" ref={orgDropdownRef}>
                  <button
                    onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition border border-purple-200"
                    data-testid="org-switcher-button"
                  >
                    <Building2 className="w-4 h-4" />
                    <span className="font-medium">{currentOrg?.name || 'Select Client'}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${showOrgDropdown ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {showOrgDropdown && (
                    <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-testid="org-dropdown">
                      <div className="p-2 border-b border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase px-2">Switch Client</p>
                      </div>
                      <div className="py-1 max-h-64 overflow-y-auto">
                        {organizations.map((org) => (
                          <button
                            key={org.id}
                            onClick={() => switchOrganization(org)}
                            disabled={switchingOrg}
                            className={`flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50 transition ${
                              currentOrg?.id === org.id ? 'bg-purple-50' : ''
                            }`}
                            data-testid={`org-option-${org.slug}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                                org.name === 'Walgreens' ? 'bg-red-500' :
                                org.name === 'Microsoft' ? 'bg-blue-500' :
                                org.name === 'Hilton' ? 'bg-indigo-500' :
                                'bg-gray-500'
                              }`}>
                                {org.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{org.name}</p>
                                <p className="text-xs text-gray-500">{org.settings?.industry || 'General'}</p>
                              </div>
                            </div>
                            {currentOrg?.id === org.id && (
                              <Check className="w-4 h-4 text-purple-600" />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <button 
                onClick={handleLogout} 
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition font-medium"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          </div>
        </header>
        
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Message */}
          {currentOrg && (
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 mb-6 text-white shadow-lg" data-testid="welcome-banner">
              <h2 className="text-2xl font-bold">Welcome, {currentOrg.name}!</h2>
              <p className="text-purple-100 mt-1">Here's your Trust Index overview for today</p>
            </div>
          )}
          
          <div className="bg-white rounded-xl shadow-lg p-12 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-24 h-24 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Trust Score Data Yet</h3>
            <p className="text-gray-600 mb-6">
              {currentOrg?.name ? `${currentOrg.name} doesn't have any Trust Score data yet.` : 'No data available.'}<br/>
              Switch to another client to get started.
            </p>
            <div className="flex justify-center gap-4">
              <button
                onClick={() => setShowOrgDropdown(true)}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition flex items-center gap-2"
              >
                <Building2 className="w-4 h-4" />
                Switch Client
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40" data-testid="dashboard-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Top Row - Title, Org Switcher, and Account */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">
                  Trust Index Dashboard
                </h1>
                <p className="text-sm text-gray-500">Real-time brand trust measurement</p>
              </div>
              
              {/* Organization/Client Switcher */}
              <div className="relative" ref={orgDropdownRef}>
                <button
                  onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition border border-purple-200"
                  data-testid="org-switcher-button"
                >
                  <Building2 className="w-4 h-4" />
                  <span className="font-medium">{currentOrg?.name || 'Select Client'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showOrgDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {/* Dropdown */}
                {showOrgDropdown && (
                  <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-testid="org-dropdown">
                    <div className="p-2 border-b border-gray-100">
                      <p className="text-xs font-medium text-gray-500 uppercase px-2">Switch Client</p>
                    </div>
                    <div className="py-1 max-h-64 overflow-y-auto">
                      {organizations.map((org) => (
                        <button
                          key={org.id}
                          onClick={() => switchOrganization(org)}
                          disabled={switchingOrg}
                          className={`flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50 transition ${
                            currentOrg?.id === org.id ? 'bg-purple-50' : ''
                          }`}
                          data-testid={`org-option-${org.slug}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
                              org.name === 'Walgreens' ? 'bg-red-500' :
                              org.name === 'Microsoft' ? 'bg-blue-500' :
                              org.name === 'Hilton' ? 'bg-indigo-500' :
                              'bg-gray-500'
                            }`}>
                              {org.name.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{org.name}</p>
                              <p className="text-xs text-gray-500">{org.settings?.industry || 'General'}</p>
                            </div>
                          </div>
                          {currentOrg?.id === org.id && (
                            <Check className="w-4 h-4 text-purple-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Anomaly Badge */}
              <AnomalyBadge onClick={() => setShowAnomalies(true)} refreshTrigger={anomalyRefresh} />
              
              <button 
                onClick={handleLogout} 
                className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition font-medium"
                data-testid="logout-button"
              >
                Logout
              </button>
            </div>
          </div>

          {/* Bottom Row - Actions Toolbar */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-gray-100">
            {/* Left Group - Filters & Refresh */}
            <div className="flex items-center gap-2">
              {/* Date Range Selector */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 appearance-none bg-white cursor-pointer text-sm"
                  data-testid="date-range-selector"
                >
                  <option value="7d">Last 7 Days</option>
                  <option value="30d">Last 30 Days</option>
                  <option value="90d">Last 90 Days</option>
                  <option value="1y">Last Year</option>
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              </div>

              {/* Refresh Button */}
              <button
                onClick={loadDashboardData}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition"
                title="Refresh data"
                data-testid="refresh-button"
              >
                <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              </button>

              {/* Recalculate Button */}
              <button
                onClick={recalculateScore}
                disabled={calculating}
                className="flex items-center gap-2 px-3 py-2 text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                title="Recalculate Trust Score"
                data-testid="recalculate-button"
              >
                <Calculator className={`w-4 h-4 ${calculating ? 'animate-pulse' : ''}`} />
                <span className="hidden sm:inline">{calculating ? 'Calculating...' : 'Recalculate'}</span>
              </button>
            </div>

            {/* Right Group - Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Data Ingestion Button - Disabled */}
              <button
                disabled
                className="flex items-center gap-2 px-3 py-2 text-gray-400 bg-gray-100 rounded-lg cursor-not-allowed text-sm font-medium opacity-50"
                data-testid="data-ingestion-button"
                title="Coming soon"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Add Data</span>
              </button>

              {/* Compare Periods Button */}
              <button
                onClick={() => setShowCompare(true)}
                className="flex items-center gap-2 px-3 py-2 text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition text-sm font-medium"
                data-testid="compare-periods-button"
              >
                <BarChart3 className="w-4 h-4" />
                <span className="hidden sm:inline">Compare</span>
              </button>

              {/* What-If Planner Button */}
              <button
                onClick={() => setShowWhatIf(true)}
                className="flex items-center gap-2 px-3 py-2 text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition text-sm font-medium"
                data-testid="what-if-button"
              >
                <Sparkles className="w-4 h-4" />
                <span className="hidden sm:inline">What-If</span>
              </button>

              {/* Insights Button */}
              <button
                onClick={() => navigate('/insights')}
                className="flex items-center gap-2 px-3 py-2 text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg transition text-sm font-medium"
                data-testid="insights-button"
              >
                <Activity className="w-4 h-4" />
                <span className="hidden sm:inline">Insights</span>
              </button>

              {/* Business Outcomes Button */}
              <button
                onClick={() => navigate('/business-outcomes')}
                className="flex items-center gap-2 px-3 py-2 text-emerald-600 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition text-sm font-medium"
                data-testid="business-outcomes-button"
              >
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Business</span>
              </button>

              {/* Departments Button */}
              <button
                onClick={() => navigate('/departments')}
                className="flex items-center gap-2 px-3 py-2 text-cyan-600 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition text-sm font-medium"
                data-testid="departments-button"
              >
                <Layers className="w-4 h-4" />
                <span className="hidden sm:inline">Departments</span>
              </button>
              
              {/* Export Dropdown */}
              <div className="relative group">
                <button
                  className="flex items-center gap-2 px-3 py-2 text-green-600 bg-green-50 hover:bg-green-100 rounded-lg transition disabled:opacity-50 text-sm font-medium"
                  disabled={exporting}
                  data-testid="export-button"
                >
                  {exporting ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Export</span>
                  <ChevronDown className="w-3 h-3" />
                </button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <button
                    onClick={exportCSV}
                    disabled={exporting}
                    className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 transition first:rounded-t-lg"
                    data-testid="export-csv-button"
                  >
                    <FileText className="w-4 h-4 text-gray-500" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Export CSV</p>
                      <p className="text-xs text-gray-500">Download raw data</p>
                    </div>
                  </button>
                  <button
                    onClick={exportPDF}
                    disabled={exporting}
                    className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 transition border-t border-gray-100"
                    data-testid="export-pdf-button"
                  >
                    <FileText className="w-4 h-4 text-red-500" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Export PDF</p>
                      <p className="text-xs text-gray-500">Dashboard snapshot</p>
                    </div>
                  </button>
                  <button
                    onClick={() => navigate('/export')}
                    className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 transition last:rounded-b-lg border-t border-gray-100"
                    data-testid="advanced-export-button"
                  >
                    <BarChart3 className="w-4 h-4 text-purple-500" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Advanced Export & Share</p>
                      <p className="text-xs text-gray-500">Reports, Excel, Share links</p>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" ref={dashboardRef}>
        {/* Welcome Message */}
        {currentOrg && (
          <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 mb-6 text-white shadow-lg" data-testid="welcome-banner">
            <h2 className="text-2xl font-bold">Welcome, {currentOrg.name}!</h2>
            <p className="text-purple-100 mt-1">Here's your Trust Index overview for today</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="dashboard-error">
            {error}
          </div>
        )}

        {calculationMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center gap-3" data-testid="calculation-message">
            <Calculator className="w-5 h-5" />
            {calculationMessage}
          </div>
        )}

        {/* Hero Section - Trust Score Gauge */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-testid="trust-score-section">
          <TrustScoreGauge 
            score={summary.trust_score} 
            comparison={summary.comparison}
            lastUpdated={summary.last_updated}
          />
        </div>

        {/* Pillar Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <PillarCard
            name="Community"
            score={summary.community_score}
            delta={summary.comparison?.community_delta}
            icon="👥"
            color="bg-blue-500"
            onDrillDown={handlePillarDrillDown}
          />
          <PillarCard
            name="Authenticity"
            score={summary.authenticity_score}
            delta={summary.comparison?.authenticity_delta}
            icon="✨"
            color="bg-purple-500"
            onDrillDown={handlePillarDrillDown}
          />
          <PillarCard
            name="Accountability"
            score={summary.accountability_score}
            delta={summary.comparison?.accountability_delta}
            icon="🎯"
            color="bg-green-500"
            onDrillDown={handlePillarDrillDown}
          />
        </div>

        {/* Trend Chart */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-testid="trend-chart-section">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-gray-900">Trust Score Trend</h2>
            <div className="text-sm text-gray-500">
              {trendData.length > 0 
                ? `${trendData.length} data points` 
                : 'No trend data available for selected period'}
            </div>
          </div>
          
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="trustGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0f172a" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0f172a" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="communityGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  stroke="#9ca3af"
                />
                <YAxis domain={[0, 100]} stroke="#9ca3af" />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  formatter={(value) => [value?.toFixed(1) || '—', '']}
                  contentStyle={{ 
                    background: 'white', 
                    border: 'none', 
                    borderRadius: '12px',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="trust_score" 
                  stroke="#0f172a" 
                  strokeWidth={3}
                  fill="url(#trustGradient)"
                  name="Trust Score"
                />
                <Line 
                  type="monotone" 
                  dataKey="community" 
                  stroke="#3b82f6" 
                  strokeWidth={2}
                  name="Community"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="authenticity" 
                  stroke="#a855f7" 
                  strokeWidth={2}
                  name="Authenticity"
                  dot={false}
                />
                <Line 
                  type="monotone" 
                  dataKey="accountability" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Accountability"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center bg-gray-50 rounded-xl">
              <div className="text-center">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <p className="text-gray-500 mb-2">No trend data available</p>
                <p className="text-sm text-gray-400">Trust scores will appear here once calculated over time</p>
              </div>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white shadow-lg">
            <h3 className="font-semibold mb-2">Data Completeness</h3>
            <p className="text-4xl font-bold mb-1">{summary.data_completeness?.toFixed(1) || 0}%</p>
            <p className="text-sm text-purple-100">Metric coverage for current period</p>
          </div>

          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white shadow-lg">
            <h3 className="font-semibold mb-2">Period</h3>
            <p className="text-lg font-bold mb-1">
              {summary.period_start ? new Date(summary.period_start).toLocaleDateString() : '—'}
            </p>
            <p className="text-sm text-blue-100">
              to {summary.period_end ? new Date(summary.period_end).toLocaleDateString() : '—'}
            </p>
          </div>

          <div className="bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl p-6 text-white shadow-lg">
            <h3 className="font-semibold mb-2">Last Calculated</h3>
            <p className="text-lg font-bold mb-1">
              {summary.last_updated ? new Date(summary.last_updated).toLocaleDateString() : '—'}
            </p>
            <p className="text-sm text-indigo-100">
              {summary.last_updated ? new Date(summary.last_updated).toLocaleTimeString() : 'Never'}
            </p>
          </div>
        </div>
      </main>

      {/* Pillar Drill-Down Modal */}
      {drillDownPillar && (
        <PillarDrillDown
          pillarId={drillDownPillar.id}
          pillarName={drillDownPillar.name}
          onClose={() => setDrillDownPillar(null)}
        />
      )}

      {/* Compare Periods Modal */}
      {showCompare && (
        <ComparePeriods onClose={() => setShowCompare(false)} />
      )}

      {/* What-If Planner Modal */}
      {showWhatIf && (
        <WhatIfPlanner 
          onClose={() => setShowWhatIf(false)}
          onApplyChanges={async (changes) => {
            // Apply changes by saving metric values and recalculating
            try {
              for (const change of changes) {
                await axios.post(`/manual-entry/metrics/${change.metric_id}/values`, {
                  value: change.projected_value,
                  period_start: new Date().toISOString(),
                  period_end: new Date().toISOString(),
                  notes: 'Applied from What-If Scenario',
                  is_draft: false
                });
              }
              setShowWhatIf(false);
              await recalculateScore();
            } catch (err) {
              console.error('Failed to apply changes:', err);
            }
          }}
        />
      )}

      {/* Anomaly Panel Modal */}
      {showAnomalies && (
        <AnomalyPanel 
          onClose={() => {
            setShowAnomalies(false);
            setAnomalyRefresh(prev => prev + 1); // Trigger badge refresh
          }} 
        />
      )}

      {/* Powered by Resolvetech Solutions Badge */}
      <div 
        className="fixed bottom-4 right-4 z-50"
        data-testid="powered-by-badge"
      >
        <a
          href="https://www.resolvetechsolutions.com"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
        >
          <span className="text-xs text-gray-500 font-medium">Powered by</span>
          <img 
            src="/rts-logo.png" 
            alt="Resolvetech Solutions" 
            className="h-6 w-auto"
          />
        </a>
      </div>
    </div>
  );
}

export default DashboardV2;
